import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPaperclip, FaFilePdf, FaFileImage } from 'react-icons/fa';
import { AuthContext } from '../../authProvider.mjs';
import clarificationService from '../service/clarificationService.mjs';
import { useIsStaff } from './ClarificationBell.mjs';
import DocumentPane from '../../components/DocumentPane.mjs';
import InfectedFileNotice from '../../components/InfectedFileNotice.mjs';
import { isInfected } from '../../utils/scanStatus.mjs';
import {
  STATUS_LABELS, STATUS_PILL, roleLabel, formatWhen, isUnresolved, announceChange,
  FILE_ACCEPT, MAX_FILES, MAX_MESSAGE_LENGTH, fileProblems,
} from '../util/clarificationText.mjs';

/**
 * One clarification question as a conversation.
 *
 * Everyone who can see it can write in it until it is resolved (the user's
 * decision: all reviewers and the region's admins) - except a question to the
 * claimant, which is between them and the region's admins. Resolving and
 * passing it to someone else belong to the asking side - the server says
 * which of these this reader may do (`permissions`), and enforces it again on
 * every request.
 *
 * Polls every 15 seconds while the tab is visible, so a reply shows up without
 * a reload. Opening it marks it read.
 */
const POLL_MS = 15000;

const AttachmentChip = ({ file, onOpen, audience }) => {
  if (isInfected(file)) {
    return (
      <div className="mt-1">
        <span className="text-xs text-gray-600 mr-2 break-all">{file.fileName}</span>
        <InfectedFileNotice audience={audience} className="inline-block" />
      </div>
    );
  }
  const Icon = file.fileType === 'image' ? FaFileImage : FaFilePdf;
  return (
    <button
      type="button"
      onClick={() => onOpen(file)}
      disabled={!file.url}
      title={file.url ? `Open ${file.fileName}` : 'This file cannot be opened right now'}
      className="mt-1 mr-2 inline-flex items-center gap-1.5 max-w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded-md text-gray-800 hover:bg-gray-50 disabled:opacity-50"
    >
      <Icon className="shrink-0 text-stanford-red" aria-hidden="true" />
      <span className="truncate">{file.fileName}</span>
    </button>
  );
};

const ClarificationThread = ({ threadId, showSubjectLink = true, onChanged }) => {
  const { user } = useContext(AuthContext);
  const audience = useIsStaff() ? 'reviewer' : 'claimant';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [redirectTo, setRedirectTo] = useState('');
  const [viewing, setViewing] = useState(null);
  const fileInput = useRef(null);
  const endRef = useRef(null);
  const lastCount = useRef(0);

  // Held in a ref so a parent's inline callback cannot restart the poll.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const next = await clarificationService.get(threadId);
      setData(next);
      setError(null);
      const count = next?.messages?.length || 0;
      if (count !== lastCount.current) {
        lastCount.current = count;
        await clarificationService.markRead(threadId);
        announceChange();
      }
    } catch (err) {
      if (!quiet) setError(err.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    lastCount.current = 0;
    load();
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') load({ quiet: true });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [data?.messages?.length]);

  const applyResult = (next) => {
    setData(next);
    lastCount.current = next?.messages?.length || 0;
    announceChange();
    onChangedRef.current?.(next);
  };

  const send = async () => {
    const text = body.trim();
    const problem = fileProblems(files);
    if (problem) { setActionError(problem); return; }
    if (!text && files.length === 0) return;
    setSending(true);
    setActionError(null);
    try {
      applyResult(await clarificationService.reply(threadId, { body: text, files }));
      setBody('');
      setFiles([]);
      if (fileInput.current) fileInput.current.value = '';
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSending(false);
    }
  };

  const resolve = async () => {
    setSending(true);
    setActionError(null);
    try {
      applyResult(await clarificationService.resolve(threadId, resolveNote.trim()));
      setResolving(false);
      setResolveNote('');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSending(false);
    }
  };

  const redirect = async () => {
    if (!redirectTo) return;
    setSending(true);
    setActionError(null);
    try {
      applyResult(await clarificationService.redirect(threadId, redirectTo));
      setRedirectTo('');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSending(false);
    }
  };

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list || [])];
    const problem = fileProblems(next);
    setActionError(problem);
    if (!problem) setFiles(next);
    if (fileInput.current) fileInput.current.value = '';
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-10" role="status">
        <div className="w-7 h-7 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin" />
        <span className="sr-only">Loading the question</span>
      </div>
    );
  }
  if (error && !data) {
    return <p className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{error}</p>;
  }
  if (!data) return null;

  const { thread, messages = [], subject, permissions = {} } = data;
  const open = isUnresolved(thread);
  const tooLong = body.length > MAX_MESSAGE_LENGTH;

  return (
    <div className="flex flex-col min-h-0">
      {/* Who asked whom, and where it stands */}
      <div className="pb-3 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_PILL[thread.status] || STATUS_PILL.closed}`}>
            {STATUS_LABELS[thread.status] || thread.status}
          </span>
          {thread.subjectLabel && (
            <span className="text-sm font-medium text-gray-900 break-words">{thread.subjectLabel}</span>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-600">
          Asked by <strong>{thread.askedByName || 'someone'}</strong> ({roleLabel(thread.askedByRole)})
          {' '}to <strong>{thread.directedToLabel}</strong> · {formatWhen(thread.createdAt)}
        </p>
        {showSubjectLink && subject?.path && !subject.missing && (
          <Link to={subject.path} className="mt-1 inline-block text-xs text-stanford-red hover:underline">
            {subject.subjectType === 'report' ? 'Open the report' : 'Open the claim'} ›
          </Link>
        )}
      </div>

      {/* The conversation */}
      <ol className="py-3 space-y-3" aria-label="Messages">
        {messages.map((m) => {
          if (m.kind === 'system') {
            return (
              <li key={m.id} className="text-center text-xs text-gray-500 italic px-4">
                {m.body} · {formatWhen(m.createdAt)}
              </li>
            );
          }
          const mine = String(m.authorId) === String(user?.id);
          return (
            <li key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[90%] sm:max-w-[80%] rounded-xl px-3 py-2 ${mine ? 'bg-red-50 border border-red-100' : 'bg-gray-50 border border-gray-200'}`}>
                <p className="text-xs text-gray-600">
                  <strong className="text-gray-800">{mine ? 'You' : (m.authorName || 'Someone')}</strong>
                  {' '}· {roleLabel(m.authorRole)}
                  {/* An admin answering about their own claim; a claimant's
                      role already reads "Claimant". */}
                  {m.isClaimant && m.authorRole !== 'user' && (
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800 rounded" title="This person's own claim">
                      Claimant
                    </span>
                  )}
                  {' '}· {formatWhen(m.createdAt)}
                </p>
                {m.body && <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">{m.body}</p>}
                {m.attachments?.length > 0 && (
                  <div className="mt-1">
                    {m.attachments.map((f) => <AttachmentChip key={f.id} file={f} onOpen={setViewing} audience={audience} />)}
                  </div>
                )}
              </div>
            </li>
          );
        })}
        <li ref={endRef} aria-hidden="true" />
      </ol>

      {!open && (
        <p className="mb-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          {thread.status === 'resolved'
            ? `Resolved by ${thread.resolvedByName || 'the asker'}${thread.resolutionNote ? `: ${thread.resolutionNote}` : '.'}`
            : `Closed${thread.closedReason ? `: ${thread.closedReason}` : '.'}`}
          {' '}Ask a new question if something is still unclear.
        </p>
      )}

      {actionError && (
        <p role="alert" className="mb-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">
          {actionError}
        </p>
      )}

      {open && permissions.canPost && (
        <div className="border-t border-gray-200 pt-3">
          <label htmlFor={`reply-${threadId}`} className="sr-only">Your reply</label>
          <textarea
            id={`reply-${threadId}`}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(); }}
            rows={3}
            placeholder={permissions.isAddressee ? 'Write your answer...' : 'Write a reply...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stanford-red"
          />
          {files.length > 0 && (
            <ul className="mt-1 flex flex-wrap gap-1.5" aria-label="Files to send">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 rounded">
                  <span className="truncate max-w-[180px]">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    aria-label={`Remove ${f.name}`}
                    className="text-gray-500 hover:text-gray-800"
                  >
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={FILE_ACCEPT}
                onChange={(e) => addFiles(e.target.files)}
                className="hidden"
                id={`files-${threadId}`}
              />
              <label
                htmlFor={`files-${threadId}`}
                className={`min-h-[44px] sm:min-h-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer ${files.length >= MAX_FILES ? 'opacity-50 pointer-events-none' : ''}`}
              >
                <FaPaperclip aria-hidden="true" /> Attach
              </label>
              <span className={`text-xs tabular-nums ${tooLong ? 'text-red-700' : 'text-gray-400'}`}>
                {body.length}/{MAX_MESSAGE_LENGTH}
              </span>
            </div>
            <button
              type="button"
              onClick={send}
              disabled={sending || tooLong || (!body.trim() && files.length === 0)}
              className="min-h-[44px] sm:min-h-0 px-4 py-1.5 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">PDF, JPG or PNG · up to {MAX_FILES} files · Ctrl+Enter sends</p>
        </div>
      )}

      {open && (permissions.canResolve || (permissions.canRedirect && permissions.redirectOptions?.length > 0)) && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
          {permissions.canRedirect && permissions.redirectOptions?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor={`redirect-${threadId}`} className="text-xs text-gray-600">Pass this question to</label>
              <select
                id={`redirect-${threadId}`}
                value={redirectTo}
                onChange={(e) => setRedirectTo(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1"
              >
                <option value="">Choose…</option>
                {permissions.redirectOptions.map((o) => (
                  <option key={`${o.value}-${o.userId || 'group'}`} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={redirect}
                disabled={!redirectTo || sending}
                className="min-h-[44px] sm:min-h-0 px-3 py-1 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Pass on
              </button>
            </div>
          )}

          {permissions.canResolve && !resolving && (
            <button
              type="button"
              onClick={() => setResolving(true)}
              className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              ✓ Mark resolved
            </button>
          )}
          {permissions.canResolve && resolving && (
            <div className="space-y-2">
              <label htmlFor={`resolve-${threadId}`} className="block text-xs text-gray-600">
                A note for the record (optional) - it goes into the report archive with the conversation.
              </label>
              <input
                id={`resolve-${threadId}`}
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                maxLength={1000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="e.g. Explained - the second taxi was the return trip"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={resolve}
                  disabled={sending}
                  className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {sending ? 'Saving…' : 'Resolve'}
                </button>
                <button
                  type="button"
                  onClick={() => { setResolving(false); setResolveNote(''); }}
                  className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* One attachment, full size. Above the walkthrough (9000) and the drawer (9200). */}
      {viewing && (
        <div
          className="fixed inset-0 z-[9400] bg-black/70 flex items-center justify-center p-2 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label={viewing.fileName}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setViewing(null); }}
        >
          <div className="bg-gray-900 rounded-xl overflow-hidden w-full max-w-5xl h-[85dvh] flex flex-col">
            <div className="flex justify-end bg-gray-800 px-2 py-1">
              <button
                type="button"
                onClick={() => setViewing(null)}
                className="min-h-[44px] sm:min-h-0 px-3 py-1 text-sm text-white hover:text-gray-300"
              >
                Close
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <DocumentPane
                url={viewing.url}
                fileType={viewing.fileType}
                fileName={viewing.fileName}
                signError={viewing.signError}
                infected={isInfected(viewing)}
                title={viewing.fileName}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClarificationThread;
